import styled, { css } from 'styled-components';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

/**
 * Peças visuais compartilhadas pela etapa de ingressos.
 *
 * Os cinzas de texto são os escurecidos do painel de vendas (≥4,5:1 sobre
 * branco); `colors.night` reprova AA em texto pequeno. O vermelho de erro é o
 * do padrão de formulário do painel.
 */
export const INK = colors.nightDark;
export const INK_SOFT = '#4A4A52';
export const MUTED = '#5A5A5A';
export const LINE = '#E2E2E6';
export const ERROR = '#B3261E';
export const accent = panelAccent(colors.occasionalPurple);
export const accentAlpha = alpha => panelAccentAlpha(colors.occasionalPurple, alpha);

export const visuallyHidden = css`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

export const SectionTitle = styled.h3`
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${MUTED};
`;

export const FieldError = styled.p`
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.35;
    color: ${ERROR};

    svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

export const FieldHint = styled.p`
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: ${MUTED};
`;

export const Chip = styled.span.withConfig({
    shouldForwardProp: prop => !['bg', 'fg', 'border', 'dashed'].includes(prop),
})`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    background: ${({ bg }) => bg || '#F5F5F7'};
    color: ${({ fg }) => fg || MUTED};
    border: 1px ${({ dashed }) => (dashed ? 'dashed' : 'solid')} ${({ border }) => border || LINE};
`;

export const IconButton = styled.button.withConfig({
    shouldForwardProp: prop => !['danger', 'active'].includes(prop),
})`
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 10px;
    border: 1px solid ${({ active }) => (active ? accent : '#D9D9E0')};
    background: #fff;
    color: ${({ danger, active }) => (danger ? ERROR : active ? accent : '#3A3A42')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
        background: ${({ danger }) => (danger ? '#FCEEEE' : '#F6F5FA')};
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;

export const OutlineButton = styled.button`
    min-height: 44px;
    padding: 0 16px;
    border-radius: 10px;
    border: 1px solid #D9D9E0;
    background: #fff;
    color: ${INK};
    font-size: 14px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
        background: #F6F5FA;
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;

export const TextButton = styled.button`
    min-height: 44px;
    padding: 0 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${accent};
    font-size: 14px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    &:hover {
        background: ${accentAlpha('0D')};
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;

export const DashedButton = styled.button`
    width: 100%;
    min-height: 52px;
    padding: 8px 14px;
    border: 1.5px dashed #CFCFD6;
    border-radius: 12px;
    background: #FCFCFD;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
    cursor: pointer;
    color: ${accent};
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
        border-color: ${accentAlpha('66')};
        background: ${accentAlpha('05')};
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 2px;
    }
`;

export const Bar = styled.span.withConfig({
    shouldForwardProp: prop => !['track', 'fill', 'percent'].includes(prop),
})`
    display: block;
    flex: 1;
    height: 6px;
    border-radius: 99px;
    overflow: hidden;
    background: ${({ track }) => track};

    &::after {
        content: '';
        display: block;
        height: 100%;
        width: ${({ percent }) => Math.min(Math.max(percent, 0), 100)}%;
        background: ${({ fill }) => fill};
    }
`;
